import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

// Major US cities for autocomplete
const US_CITIES = [
  // Texas
  "Austin, TX", "Houston, TX", "Dallas, TX", "San Antonio, TX", "Fort Worth, TX",
  "Arlington, TX", "Plano, TX", "Irving, TX", "Frisco, TX", "El Paso, TX",
  "Corpus Christi, TX", "Waco, TX", "Temple, TX", "Killeen, TX", "Belton, TX",
  "Lubbock, TX", "Amarillo, TX", "McAllen, TX", "Brownsville, TX", "Laredo, TX",
  // California
  "Los Angeles, CA", "San Francisco, CA", "San Diego, CA", "Sacramento, CA", 
  "San Jose, CA", "Oakland, CA", "Fresno, CA", "Long Beach, CA", "Bakersfield, CA",
  "Anaheim, CA", "Santa Ana, CA", "Riverside, CA", "Irvine, CA",
  // Florida
  "Miami, FL", "Orlando, FL", "Tampa, FL", "Jacksonville, FL", "Fort Lauderdale, FL",
  "St. Petersburg, FL", "Hialeah, FL", "Tallahassee, FL", "Cape Coral, FL",
  "Fort Myers, FL", "Pembroke Pines, FL", "Hollywood, FL", "Gainesville, FL",
  // New York
  "New York City, NY", "Buffalo, NY", "Rochester, NY", "Albany, NY", "Syracuse, NY",
  "Yonkers, NY", "White Plains, NY", "New Rochelle, NY", "Binghamton, NY",
  // Michigan
  "Detroit, MI", "Grand Rapids, MI", "Ann Arbor, MI", "Lansing, MI", "Flint, MI",
  "Dearborn, MI", "Warren, MI", "Sterling Heights, MI", "Kalamazoo, MI",
  // Nevada
  "Las Vegas, NV", "Reno, NV", "Henderson, NV", "North Las Vegas, NV", "Sparks, NV",
  // Other major cities
  "Chicago, IL", "Philadelphia, PA", "Phoenix, AZ", "Columbus, OH", "Charlotte, NC",
  "Indianapolis, IN", "Seattle, WA", "Denver, CO", "Boston, MA", "Nashville, TN",
  "Baltimore, MD", "Louisville, KY", "Portland, OR", "Milwaukee, WI", "Las Vegas, NV",
  "Atlanta, GA", "Memphis, TN", "New Orleans, LA", "Cleveland, OH", "Minneapolis, MN",
  "Kansas City, MO", "St. Louis, MO", "Pittsburgh, PA", "Cincinnati, OH", "Raleigh, NC",
  "Virginia Beach, VA", "Oklahoma City, OK", "Tucson, AZ", "Omaha, NE", "Miami Beach, FL",
  // Remote options
  "Remote", "Remote - United States", "Hybrid", "Nationwide"
];

const LocationAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Enter city or zip code",
  className = "",
  required = false,
  id = "location",
  ...props 
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (newValue.length > 1) {
      const filtered = US_CITIES.filter(city =>
        city.toLowerCase().includes(newValue.toLowerCase())
      ).slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setHighlightIndex(-1);
    
    // Pass value up to parent
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setInputValue(suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
    if (onChange) {
      onChange(suggestion);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestions[highlightIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length > 1 && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className="pl-10"
          required={required}
          autoComplete="off"
          data-testid="location-autocomplete-input"
          {...props}
        />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${
                index === highlightIndex ? 'bg-primary/10 text-primary' : 'text-gray-700'
              }`}
            >
              <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span>{suggestion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
